import {
  BarChart3,
  BookOpen,
  CalendarDays,
  ClipboardList,
  ClipboardPen,
  FileSpreadsheet,
  FolderOpen,
  GraduationCap,
  LayoutDashboard,
  Link2,
  Megaphone,
  MessagesSquare,
  PackageOpen,
  PenLine,
  Repeat,
  ScrollText,
  Settings,
  Target,
  Users,
} from 'lucide-react';
import { MANAGERS, OBSERVERS, STAFF, SUPER } from '@/features/auth/access';
import type { Role } from '@/types/api';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  /**
   * Roles a qui l'entree est proposee. Absent = tous ceux qui voient le
   * groupe. Une entree ne doit jamais etre offerte a qui la route refuse :
   * proposer une porte fermee, c'est faire passer un refus de droits pour une
   * panne. La valeur reprend la garde de la route, jamais une autre.
   */
  roles?: Role[];
}

/**
 * Les entrees sont regroupees par nature de tache : un agent cherche « ou
 * est-ce que je me forme » ou « ou est-ce que j'echange », pas une liste de
 * treize liens. Ce qui releve de l'agent lui-meme — profil, certificats,
 * aide, langue, theme — vit dans le menu du compte, en pied de barre.
 *
 * Entries are grouped by the kind of task they serve; what belongs to the
 * person lives in the account menu at the foot of the sidebar.
 */
export interface NavGroup {
  titleKey: string;
  items: NavItem[];
}

export const LEARNER_GROUPS: NavGroup[] = [
  {
    titleKey: 'nav.groups.training',
    items: [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
      { to: '/learn', labelKey: 'nav.learn', icon: BookOpen },
      { to: '/assignments', labelKey: 'nav.assignments', icon: ClipboardPen },
      { to: '/revision', labelKey: 'nav.revision', icon: Repeat },
      { to: '/classrooms', labelKey: 'nav.classrooms', icon: CalendarDays },
      { to: '/skills', labelKey: 'nav.skills', icon: Target },
    ],
  },
  {
    titleKey: 'nav.groups.community',
    items: [
      { to: '/forum', labelKey: 'nav.forum', icon: MessagesSquare },
      { to: '/announcements', labelKey: 'nav.announcements', icon: Megaphone },
    ],
  },
];

export const ADMIN_GROUPS: NavGroup[] = [
  {
    titleKey: 'nav.groups.steering',
    items: [
      { to: '/admin', labelKey: 'nav.analytics', icon: BarChart3, end: true, roles: OBSERVERS },
      { to: '/admin/reports', labelKey: 'nav.reports', icon: FileSpreadsheet, roles: OBSERVERS },
      { to: '/admin/audit', labelKey: 'nav.auditLogs', icon: ScrollText, roles: MANAGERS },
    ],
  },
  {
    titleKey: 'nav.groups.people',
    items: [
      { to: '/admin/users', labelKey: 'nav.users', icon: Users, roles: OBSERVERS },
      { to: '/admin/cohorts', labelKey: 'nav.cohorts', icon: GraduationCap, roles: OBSERVERS },
    ],
  },
  {
    titleKey: 'nav.groups.content',
    items: [
      { to: '/admin/content', labelKey: 'nav.content', icon: ClipboardList, roles: OBSERVERS },
      { to: '/admin/files', labelKey: 'nav.files', icon: FolderOpen, roles: STAFF },
      { to: '/admin/assignments', labelKey: 'nav.adminAssignments', icon: ClipboardPen, roles: OBSERVERS },
      { to: '/admin/grading', labelKey: 'nav.grading', icon: PenLine, roles: STAFF },
      { to: '/admin/competencies', labelKey: 'nav.competencies', icon: Target, roles: OBSERVERS },
      { to: '/admin/scorm', labelKey: 'nav.scorm', icon: PackageOpen, roles: OBSERVERS },
    ],
  },
  {
    titleKey: 'nav.groups.platform',
    items: [
      { to: '/admin/lti', labelKey: 'nav.lti', icon: Link2, roles: SUPER },
      { to: '/admin/settings', labelKey: 'nav.settings', icon: Settings, roles: MANAGERS },
    ],
  },
];

/**
 * Groupes reduits a ce que le role peut reellement ouvrir ; un groupe vide
 * disparait plutot que de laisser un titre sans entree.
 * Groups narrowed to what the role can actually open.
 */
export function groupsFor(groups: NavGroup[], role: Role | undefined): NavGroup[] {
  if (!role) return [];
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => !item.roles || item.roles.includes(role)) }))
    .filter((group) => group.items.length > 0);
}
